import { findArea } from "@/api/data";
import { IAreaItem } from "@/api/data/dto";

class AreaSharedCacheHandler {
  private readonly listMap: Map<number, IAreaItem[]> = new Map();
  private readonly idMap: Map<number, IAreaItem> = new Map();
  private readonly leafSet: Set<number> = new Set<number>();
  private readonly parentLockSet: Set<number> = new Set();
  private readonly lockSet: Set<number> = new Set();

  isLeaf(id: number): boolean {
    return this.leafSet.has(id);
  }

  getChildren(parentId: number): IAreaItem[] | undefined {
    return this.listMap.get(parentId);
  }

  getItem(id: number): IAreaItem | undefined {
    return this.idMap.get(id);
  }

  async load(id: number): Promise<IAreaItem> {
    const area = this.idMap.get(id);
    if (area !== undefined) {
      return area;
    }
    if (this.lockSet.has(id)) {
      return await new Promise<IAreaItem>((resolve, reject) => {
        setTimeout(() => {
          this.load(id).then((it) => resolve(it));
        }, 0);
      });
    }
    this.lockSet.add(id);
    const items: IAreaItem[] = await findArea({ ids: [id] });
    this.lockSet.delete(id);
    if (!items.length) {
      throw new Error("该地区不存在");
    }
    const item = items[0];
    this.idMap.set(id, item);
    return item;
  }

  async loadByList(ids: number[]): Promise<void[] | undefined> {
    if (!ids.length) {
      return;
    }
    const toFetch = ids.filter((it) => {
      if (!this.idMap.has(it)) {
        return true;
      }
      const item = this.idMap.get(it);
      return item === undefined || item === null;
    });
    if (!toFetch.length) {
      return;
    }
    const lockedList = toFetch.filter((it) => this.lockSet.has(it));
    const nowFetch = toFetch.filter((it) => !this.lockSet.has(it));
    nowFetch.forEach((it) => this.lockSet.add(it));
    const areaList = await findArea({ ids: nowFetch });
    nowFetch.forEach((it) => this.lockSet.delete(it));
    areaList.forEach((it) => this.idMap.set(it.id, it));
    if (lockedList.length) {
      return await this.loadByList(lockedList);
    }
    return;
  }
  async loadChildren(id: number): Promise<IAreaItem[]> {
    if (this.leafSet.has(id)) {
      return [];
    }
    const list = this.listMap.get(id);
    if (Array.isArray(list) && list.length) {
      return list;
    }
    if (this.parentLockSet.has(id)) {
      return await new Promise<IAreaItem[]>((resolve, reject) => {
        setTimeout(() => {
          this.loadChildren(id).then((it) => resolve(it));
        }, 0);
      });
    }
    this.parentLockSet.add(id);
    const items: IAreaItem[] = await findArea({ parentId: id });
    this.listMap.set(id, items);
    this.parentLockSet.delete(id);
    if (!items.length) {
      this.leafSet.add(id);
    } else {
      items.forEach((it: IAreaItem) => this.idMap.set(it.id, it));
    }
    return items;
  }

  async loadChildrenByList(ids: number[]): Promise<void[] | undefined> {
    if (!ids.length) {
      return;
    }
    const toFetch = ids.filter((it) => {
      if (this.leafSet.has(it)) {
        return false;
      }
      if (!this.listMap.has(it)) {
        return true;
      }
      const list = this.listMap.get(it);
      return !(Array.isArray(list) && Array.length);
    });
    if (!toFetch.length) {
      return;
    }

    const list = toFetch
      .map((it) => {
        const children: Promise<IAreaItem[]> = findArea({ parentId: it });
        return { id: it, children };
      })
      .map(async ({ id, children }) => {
        const items: IAreaItem[] = await children;
        this.listMap.set(id, items);
        if (!items.length) {
          this.leafSet.add(id);
        } else {
          items.forEach((it: IAreaItem) => this.idMap.set(it.id, it));
        }
      });
    return Promise.all(list);
  }

  cleanAll() {
    this.listMap.clear();
    this.idMap.clear();
  }
}

export const AreaSharedCache = new AreaSharedCacheHandler();
